/* eslint-disable react-hooks/exhaustive-deps */
import { ErrorMessage, Field } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Form, FormControl, Row, Spinner } from 'react-bootstrap';
import { FormLabel, FormStepHeader } from '../../../../../components/forms';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import { useToast } from '../../../../../context/ToastContext';
import { NumberInput } from '../../../../../components/inputs/NumberInput';
import {
  useGetCourseQuery,
  useGetCoursesSimpleQuery
} from '../../../../../services/endpoints/schedule/course';
import { Course } from '../../../../../types/course.type';
import {
  expensesCategoriesOptions,
  expensesContextOptions,
  paymentMethodOptions
} from '../../../../../utils/consts/finance/finance';
import { StepProps } from './AddEditExpense';

export const ExpenseForm = ({ touched, errors, values, setFieldValue, readOnly }: StepProps) => {
  const { data, error, isLoading } = useGetCoursesSimpleQuery({
    data: {
      orderBy: { field: 'title', order: 'asc' },
      filterBy: [{ field: 'status', operator: 'not_equals', value: 'cancelled' }],
      entries: 99999
    },
    scope: 'all'
  });

  const { addToast } = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);

  const urlCourseId = Number(values?.course_id?.value) || undefined;
  const { data: urlCourseData } = useGetCourseQuery(urlCourseId!, { skip: !urlCourseId });

  const {
    data: selected_cource_details,
    error: cource_error,
    isLoading: cource_loading
  } = useGetCourseQuery(Number(selectedCourse?.id), { skip: !selectedCourse?.id });

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading sessions', 'error');
    }
  }, [error, isLoading]);

  useEffect(() => {
    if (data?.results) {
      setCourses(data.results);
    }

    const id = Number(values?.course_id?.value);
    if (!id) return;

    const fromList = data?.results?.find((c) => Number(c.id) === id);
    const fromUrl = urlCourseData?.course;
    const match = fromList ?? (fromUrl && Number(fromUrl.id) === id ? fromUrl : null);

    if (match) {
      if (selectedCourse?.id !== match.id) {
        setSelectedCourse(match);
      }
      if (values?.course_id?.label !== match.title) {
        setFieldValue!('course_id', { value: id, label: match.title });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, urlCourseData, values?.course_id?.value]);

  if (isLoading) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <Row>
        <Col xs={12}>
          <FormStepHeader title="Expense Details" showRequiredLegend={!readOnly} />
        </Col>
      </Row>
      <Row>
        <Col
          xs={12}
          lg={6}
          className="pe-1"
        >
          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="course_id"
          >
            <Field
              as={FormSelect}
              name="course_id"
              label="Session Title"
              options={courses.map((c: Course) => ({ value: c.id, label: c.title })) ?? []}
              loadingError={error ? 'Error loading sessions' : undefined}
              required
            />
            <ErrorMessage name="course_id">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="category"
          >
            <Field
              as={FormSelect}
              name="category"
              label="Category"
              options={
                expensesCategoriesOptions.map((c: { id: string; label: string }) => ({
                  value: c.id,
                  label: c.label
                })) ?? []
              }
              required
            />
            <ErrorMessage name="category">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="amount"
          >
            <FormLabel required>Amount</FormLabel>
            <Field
              name="amount"
              step="0.01"
              min="0"
              as={NumberInput}
              isInvalid={touched.amount && !!errors.amount}
              aria-required
            />
            <Form.Text className="text-muted">
              <strong>Total:</strong> $
              {values?.amount ? parseFloat(values.amount.toString()).toFixed(2) : '0.00'}
            </Form.Text>
            <ErrorMessage name="amount">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="context"
          >
            <Field
              as={FormSelect}
              name="context"
              label="Context"
              options={
                expensesContextOptions.map((c: { id: string; label: string }) => ({
                  value: c.id,
                  label: c.label
                })) ?? []
              }
              required
            />
            <ErrorMessage name="context">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>
          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="notes"
          >
            <FormLabel>Session-Related Expense Tool Notes</FormLabel>
            <Field
              name="notes"
              type="text"
              as={Form.Control}
              isInvalid={touched.notes && !!errors.notes}
            />
            <ErrorMessage name="notes">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          {selected_cource_details?.course && (
            <Form.Text className="text-muted d-block mb-3">
              <strong>Venue Notes:</strong>{' '}
              {selected_cource_details?.course?.venue?.notes || 'No venue notes available.'}
            </Form.Text>
          )}
          {selected_cource_details?.course && (
            <Form.Text className="text-muted d-block mb-3">
              <strong>Facility Use Payment Mailing Details:</strong>{' '}
              {selected_cource_details?.course?.venue?.facility_use_fee_payment_details ||
                'No venue notes available.'}
            </Form.Text>
          )}
        </Col>

        <Col
          xs={12}
          lg={6}
          className="pe-1"
        >
          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="due_on"
          >
            <FormLabel>Due On</FormLabel>
            <Field
              name="due_on"
              type="date"
              as={Form.Control}
              isInvalid={touched.due_on && !!errors.due_on}
            />
            <ErrorMessage name="due_on">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="paid_on"
          >
            <FormLabel>Paid On</FormLabel>
            <Field
              name="paid_on"
              type="date"
              as={Form.Control}
              isInvalid={touched.paid_on && !!errors.paid_on}
            />
            <ErrorMessage name="paid_on">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="invoice_number"
          >
            <FormLabel>Invoice Number</FormLabel>
            <Field
              name="invoice_number"
              type="text"
              as={Form.Control}
              isInvalid={touched.invoice_number && !!errors.invoice_number}
            />
            <Form.Text className="text-muted d-block mb-3">&nbsp;</Form.Text>

            <ErrorMessage name="invoice_number">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="payment_method"
          >
            <Field
              as={FormSelect}
              name="payment_method"
              label="Payment Method"
              options={
                paymentMethodOptions.map((c: { id: string; label: string }) => ({
                  value: c.id,
                  label: c.label
                })) ?? []
              }
              required
            />

            <ErrorMessage name="payment_method">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="check_number"
          >
            <FormLabel>Check Number</FormLabel>
            <Field
              name="check_number"
              as={NumberInput}
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};
